import * as Sentry from '@sentry/react';

// supabase-js v2 persists the session under `sb-<projectRef>-auth-token` (see
// SupabaseClient's defaultStorageKey) and may base64-encode the value. Both the
// key format and the encoding are library internals, so every step here is
// best-effort and must never throw.
function readPersistedUser(): { id: string; email?: string } | null {
  try {
    const projectRef = new URL(import.meta.env.VITE_SUPABASE_URL).hostname.split('.')[0];
    const raw = localStorage.getItem(`sb-${projectRef}-auth-token`);
    if (!raw) return null;

    const json = raw.startsWith('base64-') ? atob(raw.slice('base64-'.length)) : raw;
    const user = JSON.parse(json)?.user;
    if (!user?.id) return null;

    return { id: user.id, email: user.email };
  } catch (e) {
    return null;
  }
}

export function initializeSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production';

  if (!dsn) {
    console.warn('Sentry DSN not configured');
    return;
  }

  Sentry.init({
    dsn,
    environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0, // 10% in production, 100% in dev

    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors

    // Release tracking
    release: import.meta.env.VITE_APP_VERSION || 'unknown',

    // Ignore specific errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      // Supabase auth errors that are handled
      'AuthApiError',
      'AuthRetryableFetchError',
    ],

    // Before sending event, add context
    beforeSend(event) {
      // Don't send events in development unless explicitly enabled
      if (environment === 'development' && !import.meta.env.VITE_SENTRY_DEBUG) {
        console.log('Sentry event (not sent in dev):', event);
        return null;
      }

      // AuthContext calls setUserContext() and is the primary source of user
      // context. This only fills the gap for errors thrown before it has
      // resolved the session, e.g. during the first moments after page load.
      if (!event.user) {
        const persistedUser = readPersistedUser();
        if (persistedUser) {
          event.user = persistedUser;
        }
      }

      return event;
    },
  });
}

// Helper to manually capture exceptions with context
export function captureException(error: Error, context?: Record<string, any>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

// Helper to capture messages
export function captureMessage(message: string, level: 'info' | 'warning' | 'error' = 'info') {
  Sentry.captureMessage(message, level);
}

// Helper to set user context
export function setUserContext(userId: string, email?: string) {
  Sentry.setUser({
    id: userId,
    email,
  });
}

// Helper to clear user context on logout
export function clearUserContext() {
  Sentry.setUser(null);
}

// Helper to add breadcrumb
export function addBreadcrumb(message: string, category: string, data?: Record<string, any>) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}
